import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ventures } from '../../data/mock';
import { ExternalLink, Palette, PawPrint, Car, Wrench, Timer } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/use-scroll-animation';

const ventureIcons = {
  'TikTym': Timer,
  'huesofred.com': Palette,
  'PawBuddy': PawPrint,
  'CarPNP': Car,
  'FounderZKit': Wrench
};

const LIVE_STATUSES = ['Active', 'Live'];

const VentureCard = ({ venture, IconComponent, index }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <Card
      ref={ref}
      className={`group border-slate-200/80 bg-white/80 backdrop-blur-sm hover:bg-white hover:shadow-premium-xl hover:border-slate-300 transition-all duration-500 overflow-hidden hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-8">
        {/* Premium Logo placeholder */}
        <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mb-6 group-hover:from-slate-900 group-hover:to-slate-800 group-hover:scale-110 transition-all duration-500 shadow-premium group-hover:shadow-premium-lg">
          <IconComponent className="w-8 h-8 text-slate-600 group-hover:text-white transition-colors duration-500 z-10 relative" />
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>
        
        {/* Venture name */}
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-xl font-bold text-slate-900 group-hover:text-slate-900 transition-colors">
            {venture.name}
          </h3>
          {venture.url && (
            <a 
              href={venture.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-600 transition-colors hover:scale-110 inline-block"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          )}
        </div>
        
        {/* Description */}
        <p className="text-slate-600 text-sm leading-relaxed mb-5 font-medium">
          {venture.description}
        </p>
        
        {/* Premium Status Badge */}
        <Badge 
          variant={LIVE_STATUSES.includes(venture.status) ? 'default' : 'secondary'}
          className={`font-semibold shadow-premium ${
            LIVE_STATUSES.includes(venture.status)
              ? 'bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 hover:from-emerald-200 hover:to-emerald-100 border-emerald-200' 
              : 'bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700 hover:from-amber-200 hover:to-amber-100 border-amber-200'
          }`}
        >
          {venture.status}
        </Badge>
      </CardContent>
    </Card>
  );
};

export const VenturesSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="ventures" className="py-32 lg:py-40 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-50/20 to-transparent rounded-full blur-3xl opacity-40 animate-float" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Premium Section Header */}
        <div 
          ref={headerRef}
          className={`max-w-3xl mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 inline-block">Our Venture Studio</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
            Built In-House. Real Products. Real Learning.
          </h2>
          <p className="mt-6 text-xl text-slate-600 leading-relaxed font-medium">
            We build for clients and ourselves. This keeps us sharp and product-focused.
          </p>
        </div>

        {/* Premium Ventures Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {ventures.map((venture, index) => {
            const IconComponent = ventureIcons[venture.name] || Wrench;
            return (
              <VentureCard
                key={venture.id}
                venture={venture}
                IconComponent={IconComponent}
                index={index}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};
